import {
    BellOutlined,
    CloseOutlined,
    EyeOutlined,
    FileOutlined,
    LockOutlined,
    MoreOutlined,
    PlusOutlined,
    PushpinOutlined
} from '@ant-design/icons'
import { createAdCampaign } from '@entities/ad-campaign/api'
import { AdCampaignType } from '@entities/ad-campaign/api/types'
import { useChannels } from '@entities/channel'
import { InternetRecourceType } from '@entities/channel/model/types'
import { usePartners } from '@entities/partner'
import { MessageView } from '@entities/post'
import { checkDraftPost } from '@entities/post/api'
import { kktuCodes } from '@entities/post/model/data/kktuCodes'
import OneContract from '@shared/assets/1-contract.svg'
import TwoContract from '@shared/assets/2-contract.svg'
import { advertisingPostType, collaborationPostType } from '@shared/lib/constants/constants'
import { dateToShortString } from '@shared/lib/dateToShortString/dateToShortString'
import { extractLinksFromHTML } from '@shared/lib/extractLinksFromHTML/extractLinksFromHTML'
import { transformToTreeData } from '@shared/lib/transformToTreeData/transformToTreeData'
import { CustomTag } from '@shared/ui/custom-tag/custom-tag'
import { IconSwitch } from '@shared/ui/icon-switch/icon-switch'
import { MobileSwitch } from '@shared/ui/mobile-switch/mobile-switch'
import type { FormProps, InputRef } from 'antd'
import {
    Avatar,
    Button,
    Col,
    Divider,
    Dropdown,
    Flex,
    Form,
    Image,
    Input,
    InputNumber,
    Radio,
    Row,
    Select,
    Space,
    theme,
    TreeSelect,
    Typography
} from 'antd'
import { Input as AntMobileInput, Popup } from 'antd-mobile'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { usePosts } from '../../model/hooks/use-posts'
import type { AdLink, AdPriceMethod, OrdResourceType, Reallocation } from '../../model/types'
import { MessageType, type Message, type PostState } from '../../model/types'
import * as styles from './add-ads-post-mobile-modal.module.scss'

const { Text, Link } = Typography

interface EditPostModalProps {
    open: boolean
    onClose: () => void
    sessionId: string
    adCampaignType: AdCampaignType
}

type Resource = {
    channelId: number
    resourceName: string
    resourceUrl: string
    resourceType: OrdResourceType
}

type FieldType = {
    title: string
    adCampaignName: string
    state: PostState
    channels: number[]
    messages: Message[]
    publishDate: Dayjs
    publishTime: Dayjs
    notify: boolean
    isPinned: boolean
    isAdvertising: boolean
    autoDeleteHours: number
    keepOnTopHours: number
    advertisingInfo: {
        budget: number
        viewToken: string
        sellerConfirm: boolean
        customerConfirm: boolean
        paymentConfirmed: boolean
        priceMethod: AdPriceMethod
        links: AdLink[]
        ord: {
            withAgent: boolean
            category: string
            agentCommission: number
            resources: Resource[]
            reallocations: Reallocation[]
            nds: boolean
            ndsPercent: number
        }
    }
    postType: {
        value: number
    }
}

export const AddAdsPostMobileModal = ({ open, adCampaignType, sessionId, onClose }: EditPostModalProps) => {
    const {
        channels,
        channelDetails,
        currentChannel,
        actions: { createChannel }
    } = useChannels()

    const {
        actions: { createPost, previewPost },
        postTypes
    } = usePosts({ channelId: currentChannel?.id })

    const {
        token: { colorWhite, colorPrimary }
    } = theme.useToken()

    const [form] = Form.useForm<FieldType>()

    const values = form.getFieldsValue()

    const text = channelDetails?.settings?.general.showSignature
        ? `</br></br>${channelDetails?.settings?.general.signature}`
        : ''

    const budget = form.getFieldValue('advertisingInfo')?.budget

    const onFinish: FormProps<FieldType>['onFinish'] = useCallback(
        async ({ adCampaignName, ...values }: FieldType) => {
            if (!currentChannel) {
                return
            }

            const dateStr = values.publishDate // '2025-06-13'
            const timeStr = values.publishTime // '14:30'

            const [hour, minute] = timeStr.split(':').map(Number)

            const publishDate = dayjs(dateStr).hour(hour).minute(minute).second(0)

            const campaign = await createAdCampaign({
                name: adCampaignName,
                type: adCampaignType,
                channel: currentChannel.id
            })

            await Promise.all(
                values.channels.map((v) => {
                    const ordResource = values.advertisingInfo.ord.resources?.find((r) => r.channelId === v)

                    return createPost({
                        ...values,
                        channels: [v],
                        publishDate: publishDate?.toDate(),
                        advertisingInfo: {
                            ...values.advertisingInfo,
                            adCampaign: campaign.id,
                            ...(isOrd && {
                                ordInfo: {
                                    ...values.advertisingInfo.ord,
                                    resourceName: ordResource?.resourceName,
                                    resourceType: ordResource?.resourceType,
                                    resourceUrl: ordResource?.resourceUrl
                                }
                            })
                        }
                    })
                })
            )

            onClose()
        },
        [adCampaignType, createPost, currentChannel, isOrd, onClose]
    )

    const onSubmit = useCallback(
        (state?: Partial<FieldType>) => {
            if (state) {
                form.setFieldsValue({
                    ...state
                })
            }

            form.submit()
        },
        [form]
    )

    const {
        partnersResources,
        actions: { refetchRartnersResources }
    } = usePartners({ loadPartnersResources: true })

    const moreActionsItems = useMemo(
        () => [
            {
                key: '1',
                label: (
                    <Space style={{ width: '100%' }}>
                        <FileOutlined />В черновики
                    </Space>
                ),
                onClick: () => onSubmit()
            },
            {
                key: '3',
                label: (
                    <Space style={{ width: '100%' }}>
                        <EyeOutlined />
                        Предпросмотр в бот
                    </Space>
                ),
                onClick: () => {
                    const dateStr = values.publishDate // '2025-06-13'
                    const timeStr = values.publishTime // '14:30'

                    const [hour, minute] = timeStr.split(':').map(Number)

                    const publishDate = dayjs(dateStr).hour(hour).minute(minute).second(0)

                    previewPost({
                        ...values,
                        publishDate: publishDate?.toDate()
                    })
                }
            }
        ],
        [onSubmit, previewPost, values]
    )

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const isSale = adCampaignType === AdCampaignType.sale

    const internetResourceOptions = useMemo(
        () => (isSale ? channels : partnersResources),
        [channels, isSale, partnersResources]
    )

    useEffect(() => {
        if (!sessionId) return
        checkDraftPost(sessionId).then((postData) => {
            if (postData) {
                form.setFieldsValue({
                    messages: [
                        {
                            ...postData.post,
                            id: Date.now(),
                            file: postData.post.file.map((file) => {
                                const item = {
                                    ...file,
                                    name: file.response.file_name,
                                    type: file.response.file_type,
                                    url: file.response.file_url
                                }
                                if (
                                    file.response.file_type !== MessageType.AUDIO &&
                                    file.response.file_type !== MessageType.VOICE &&
                                    file.response.file_type !== MessageType.DOCUMENT
                                ) {
                                    item.thumbUrl = file.response?.preview_url || file.response.file_url
                                }
                                return item
                            })
                        }
                    ]
                })
            }
        })
    }, [form, sessionId])

    useEffect(() => {
        const links = extractLinksFromHTML(values?.messages?.[0].text)

        form.setFieldsValue({
            advertisingInfo: {
                links
            }
        })
    }, [form, values?.messages])

    const [resourceName, setResourceName] = useState('')
    const inputRef = useRef<InputRef>(null)

    const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setResourceName(event.target.value)
    }

    const addResource = async (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
        e.preventDefault()
        setResourceName('')
        await createChannel({
            name: resourceName,
            type: InternetRecourceType.site,
            isFake: true
        })
        refetchRartnersResources()
    }

    const isOrd = Form.useWatch('isOrd', form)

    const navigate = useNavigate()

    const selectedChannels: number[] = Form.useWatch('channels', form) ?? []

    useEffect(() => {
        form.setFieldsValue({
            advertisingInfo: {
                ord: {
                    resources: internetResourceOptions
                        ?.filter((r) => selectedChannels.find((id) => r.id === id))
                        ?.map((r) => ({
                            channelId: r.id,
                            resourceName: r.name,
                            resourceType: 'site',
                            resourceUrl: r.url
                        }))
                }
            }
        })
    }, [selectedChannels.length])

    return (
        <Popup visible={open} onMaskClick={onClose} onClose={onClose} bodyStyle={{ background: '#F0F3F5' }}>
            <Form
                form={form}
                name="add-post"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                onFinish={onFinish}
                initialValues={{
                    state: 'draft',
                    notify: true,
                    isAdvertising: true,
                    publishDate: dayjs().format('YYYY-MM-DD'),
                    publishTime: dayjs().format('HH:mm'),
                    adCampaignName: `РК-${dateToShortString(new Date())}`,
                    advertisingInfo: {
                        withAgent: true,
                        paymentConfirmed: false,
                        ord: {
                            resources: [],
                            nds: false,
                            ndsPercent: 20,
                            reallocations: []
                        }
                    },
                    messages: [
                        {
                            file: [],
                            id: Date.now(),
                            messageType: MessageType.PHOTO,
                            multi: true,
                            isCaptionAbove: false,
                            text
                        }
                    ],
                    isOrd: false
                }}
                style={{ padding: '20px 16px', maxHeight: '100vh', overflow: 'auto' }}
            >
                <Row gutter={[10, 10]}>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Row>
                                <Col span={24} style={{ marginBottom: '24px' }}>
                                    <Flex gap={6}>
                                        <CustomTag>
                                            {budget > 0 ? advertisingPostType.name : collaborationPostType.name}
                                        </CustomTag>
                                        <Form.Item<FieldType>
                                            name="postType"
                                            required={false}
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                placeholder="Выберите тип"
                                                labelInValue
                                                optionLabelProp="label"
                                                popupMatchSelectWidth={false}
                                                popupRender={(menu) => (
                                                    <>
                                                        {menu}
                                                        <Divider style={{ margin: '8px 0' }} />
                                                        <div style={{ padding: '8px', textAlign: 'center' }}>
                                                            <Button
                                                                type="primary"
                                                                onClick={() => navigate('/settings')}
                                                            >
                                                                + Новый тип
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            >
                                                {postTypes?.map((item) => (
                                                    <Select.Option
                                                        style={{ textAlign: 'center' }}
                                                        key={item.id}
                                                        value={item.id}
                                                        label={item.name}
                                                    >
                                                        <CustomTag color={item.color}>{item.name}</CustomTag>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType> name="adCampaignName">
                                        <Input placeholder="Название рекламной компании" size="large" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType>
                                        name="channels"
                                        required={false}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                    >
                                        <Select
                                            showSearch
                                            mode="multiple"
                                            optionFilterProp="label"
                                            style={{ width: '100%' }}
                                            popupRender={(menu) => (
                                                <>
                                                    {menu}
                                                    {!isSale && (
                                                        <>
                                                            <Divider style={{ margin: '8px 0' }} />
                                                            <Space style={{ padding: '0 8px 4px' }}>
                                                                <Input
                                                                    placeholder="Введите название ресурса"
                                                                    ref={inputRef}
                                                                    value={resourceName}
                                                                    onChange={onNameChange}
                                                                    onKeyDown={(e) => e.stopPropagation()}
                                                                />
                                                                <Button
                                                                    type="text"
                                                                    icon={<PlusOutlined />}
                                                                    onClick={addResource}
                                                                >
                                                                    Добавить
                                                                </Button>
                                                            </Space>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                            size="large"
                                            popupMatchSelectWidth={false}
                                            placeholder="Выберите интернет ресурсы"
                                        >
                                            {internetResourceOptions?.map((c) => (
                                                <Select.Option value={c.id} key={c.id}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        <Avatar src={c.photoUrl} size={20} />
                                                        {c.name}
                                                    </div>
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Row gutter={[10, 10]}>
                                <Col span={24}>
                                    <Text style={{ fontSize: '17px', color: colorPrimary, whiteSpace: 'nowrap' }}>
                                        Опубликовать
                                    </Text>
                                    <Flex gap={8} align="center" style={{ marginTop: '10px' }}>
                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="publishDate"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0, marginLeft: 'auto' }}
                                            >
                                                <AntMobileInput
                                                    type="date"
                                                    placeholder="Дата публикации"
                                                    style={{ width: '120px' }}
                                                />
                                            </Form.Item>
                                        </div>
                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="publishTime"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <AntMobileInput
                                                    type="time"
                                                    placeholder="Время публикации"
                                                    style={{ width: '82px' }}
                                                />
                                            </Form.Item>
                                        </div>
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Flex gap={8} align="center" justify="space-between">
                                        <Text style={{ fontSize: '17px', color: colorPrimary, whiteSpace: 'nowrap' }}>
                                            Держать в топе
                                        </Text>
                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="keepOnTopHours"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <AntMobileInput
                                                    type="time"
                                                    placeholder="Время публикации"
                                                    style={{ width: '82px' }}
                                                />
                                            </Form.Item>
                                        </div>
                                    </Flex>
                                </Col>
                                <Col span={24}>
                                    <Flex gap={8} align="center" justify="space-between">
                                        <Text style={{ fontSize: '17px', color: colorPrimary, whiteSpace: 'nowrap' }}>
                                            Удалить через
                                        </Text>

                                        <div className={styles.inputCard}>
                                            <Form.Item<FieldType>
                                                name="autoDeleteHours"
                                                required={false}
                                                rules={[{ required: true, message: 'Обязательное поле' }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Select
                                                    placeholder="Удалить через, ч"
                                                    options={[
                                                        {
                                                            label: '12',
                                                            value: 12
                                                        },
                                                        {
                                                            label: '24',
                                                            value: 24
                                                        },
                                                        { label: '48', value: 48 },
                                                        { label: '72', value: 72 }
                                                    ]}
                                                    allowClear
                                                />
                                            </Form.Item>
                                        </div>
                                    </Flex>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Flex align="center">
                                <Text style={{ fontSize: '17px', color: colorPrimary, whiteSpace: 'nowrap' }}>
                                    Закреп/Уведомления
                                </Text>
                                <Form.Item<FieldType>
                                    name="isPinned"
                                    required={false}
                                    style={{ marginLeft: 'auto', marginBottom: 0 }}
                                >
                                    <IconSwitch text={'Закрепить'}>
                                        <PushpinOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                                <Form.Item<FieldType> name="notify" required={false} style={{ marginBottom: 0 }}>
                                    <IconSwitch text={'Отправить со звуком'}>
                                        <BellOutlined style={{ fontSize: '26px', color: colorPrimary }} />
                                    </IconSwitch>
                                </Form.Item>
                            </Flex>
                        </div>
                    </Col>
                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Form.Item name={['advertisingInfo', 'paymentConfirmed']}>
                                <Select
                                    placeholder="Выберите статус оплаты"
                                    allowClear
                                    options={[
                                        { label: 'Оплачен', value: true },
                                        { label: 'Не оплачен', value: false }
                                    ]}
                                    size="large"
                                />
                            </Form.Item>
                            <Form.Item
                                name={['advertisingInfo', 'priceMethod']}
                                rules={[
                                    {
                                        required: true,
                                        message: 'Обязательное поле'
                                    }
                                ]}
                            >
                                <div className={styles.radioGroup}>
                                    <Radio.Group
                                        options={[
                                            {
                                                label: 'Фикс',
                                                value: 'fix'
                                            },
                                            { label: 'CPM', value: 'cpm' }
                                        ]}
                                        size="large"
                                        optionType="button"
                                    />
                                </div>
                            </Form.Item>
                            <Row gutter={[8, 8]} align={'middle'}>
                                <Col span={16}>
                                    <Form.Item<FieldType>
                                        name={['advertisingInfo', 'budget']}
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            min={0}
                                            style={{ width: '100%' }}
                                            size="large"
                                            placeholder="Введите стоимость"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item<FieldType> name={['advertisingInfo', 'ord', 'nds']}>
                                        <MobileSwitch uncheckedText="Без НДС" checkedText="с НДС" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item<FieldType> name={['advertisingInfo', 'ord', 'ndsPercent']} hidden>
                                <Input />
                            </Form.Item>
                        </div>
                    </Col>
                    {form.getFieldsValue().messages?.length && (
                        <Col span={24}>
                            <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                                {values.messages?.map((item) => <MessageView message={item} key={item.id} />)}
                            </div>
                        </Col>
                    )}

                    <Col span={24}>
                        <div style={{ padding: '16px', background: '#FEFEFF', borderRadius: '12px' }}>
                            <Form.Item
                                label={<Text style={{ fontSize: '17px', color: colorPrimary }}>Режим ОРД</Text>}
                                name="isOrd"
                                valuePropName="checked"
                                labelAlign="left"
                                labelCol={{ flex: 'auto' }}
                                wrapperCol={{ flex: 'none' }}
                                style={{ marginBottom: 0 }}
                            >
                                <MobileSwitch />
                            </Form.Item>
                            {isOrd && (
                                <>
                                    <Divider style={{ margin: '16px 0' }} />
                                    <Form.Item
                                        name={['advertisingInfo', 'ord', 'withAgent']}
                                        label={
                                            <Text style={{ fontSize: '17px', color: colorPrimary }}>
                                                Выберите тип договора
                                            </Text>
                                        }
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Radio.Group
                                            className={styles.contractRadioGroup}
                                            options={[
                                                {
                                                    value: false,
                                                    label: (
                                                        <Image
                                                            src={OneContract}
                                                            preview={false}
                                                            style={{
                                                                opacity: form.getFieldsValue().advertisingInfo?.ord
                                                                    .withAgent
                                                                    ? 0.6
                                                                    : 1
                                                            }}
                                                        />
                                                    )
                                                },
                                                {
                                                    value: true,
                                                    label: (
                                                        <Image
                                                            src={TwoContract}
                                                            preview={false}
                                                            style={{
                                                                opacity: form.getFieldsValue().advertisingInfo?.ord
                                                                    .withAgent
                                                                    ? 1
                                                                    : 0.6
                                                            }}
                                                        />
                                                    )
                                                }
                                            ]}
                                        />
                                    </Form.Item>
                                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.withAgent !== curr.withAgent}>
                                        {({ getFieldValue }) =>
                                            getFieldValue(['advertisingInfo', 'ord', 'withAgent']) ? (
                                                <Form.Item
                                                    name={['advertisingInfo', 'ord', 'agentCommission']}
                                                    rules={[{ required: true, message: 'Укажите комиссию агента' }]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                        addonAfter="₽"
                                                        placeholder="Сумма комиссии агента"
                                                    />
                                                </Form.Item>
                                            ) : null
                                        }
                                    </Form.Item>
                                    <Form.List name={['advertisingInfo', 'ord', 'resources']}>
                                        {(fields) => {
                                            return (
                                                <>
                                                    {fields.map(({ key, name, ...restField }, idx) => (
                                                        <Row gutter={8} key={key} align="bottom">
                                                            <Col span={14}>
                                                                <Form.Item
                                                                    noStyle
                                                                    dependencies={[
                                                                        [
                                                                            'advertisingInfo',
                                                                            'ord',
                                                                            'resources',
                                                                            name,
                                                                            'resourceName'
                                                                        ]
                                                                    ]}
                                                                >
                                                                    {({ getFieldValue }) => {
                                                                        const resName =
                                                                            getFieldValue([
                                                                                'advertisingInfo',
                                                                                'ord',
                                                                                'resources',
                                                                                name,
                                                                                'resourceName'
                                                                            ]) || ''
                                                                        return (
                                                                            <Form.Item
                                                                                {...restField}
                                                                                label={
                                                                                    <Link style={{ fontSize: 17 }}>
                                                                                        {resName || 'Название площадки'}
                                                                                    </Link>
                                                                                }
                                                                                name={[name, 'resourceName']}
                                                                                rules={[
                                                                                    {
                                                                                        required: true,
                                                                                        message:
                                                                                            'Укажи название площадки'
                                                                                    }
                                                                                ]}
                                                                            >
                                                                                <Input
                                                                                    size="large"
                                                                                    placeholder="Название площадки"
                                                                                />
                                                                            </Form.Item>
                                                                        )
                                                                    }}
                                                                </Form.Item>
                                                            </Col>

                                                            <Col span={9}>
                                                                <Form.Item
                                                                    {...restField}
                                                                    label={
                                                                        idx === 0 ? (
                                                                            <Text
                                                                                style={{
                                                                                    color: colorPrimary,
                                                                                    fontSize: 17
                                                                                }}
                                                                            >
                                                                                Тип площадки
                                                                            </Text>
                                                                        ) : undefined
                                                                    }
                                                                    name={[name, 'resourceType']}
                                                                    valuePropName="checked"
                                                                    getValueProps={(v?: 'site' | 'app') => ({
                                                                        checked: v === 'site'
                                                                    })}
                                                                    getValueFromEvent={(checked: boolean) =>
                                                                        checked ? 'site' : 'app'
                                                                    }
                                                                >
                                                                    <MobileSwitch
                                                                        checkedText="Сайт"
                                                                        uncheckedText="Приложение"
                                                                        alwaysChecked
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                        </Row>
                                                    ))}
                                                </>
                                            )
                                        }}
                                    </Form.List>

                                    <Form.Item
                                        label={
                                            <Text style={{ fontSize: '17px', color: colorPrimary }}>
                                                Категория рекламируемого товара или услуги
                                            </Text>
                                        }
                                        className={styles.formItem}
                                        name={['advertisingInfo', 'ord', 'category']}
                                        rules={[{ required: true, message: 'Обязательное поле' }]}
                                    >
                                        <TreeSelect
                                            treeData={transformToTreeData(kktuCodes)}
                                            placeholder="Выберите категорию"
                                            treeExpandAction="click"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item name={['advertisingInfo', 'ord', 'reallocations']} hidden />
                                    {/* <Form.Item
                                        name={['advertisingInfo', 'ord', 'withReallocation']}
                                        label={
                                            <Space>
                                                <Text>Разаллокация</Text>
                                                <Tooltip title="Укажите, какая часть суммы акта/отчёта между исполнителем и заказчиком приходится на интернет-рекламу по атрибутам договора с каждым конечным рекламодателем.">
                                                    <QuestionCircleOutlined />
                                                </Tooltip>
                                            </Space>
                                        }
                                        valuePropName="checked"
                                        wrapperCol={{ span: 'auto ' }}
                                        labelCol={{ span: 'auto ' }}
                                        required
                                    >
                                        <MobileSwitch
                                            onChange={(checked) => {
                                                if (checked) {
                                                    form.setFieldsValue({
                                                        reallocations: [{}]
                                                    })
                                                } else {
                                                    form.setFieldsValue({
                                                        reallocations: []
                                                    })
                                                }
                                            }}
                                        />
                                    </Form.Item> */}

                                    {/* <Form.Item
                                        noStyle
                                        shouldUpdate={(prev, curr) => prev.withReallocation !== curr.withReallocation}
                                    >
                                        {({ getFieldValue }) =>
                                            getFieldValue(['advertisingInfo', 'ord', 'withReallocation']) ? (
                                                <Form.List name={['advertisingInfo', 'ord', 'reallocations']}>
                                                    {(fields, { add, remove }) => (
                                                        <>
                                                            {fields.map(({ key, name, ...restField }) => (
                                                                <div
                                                                    key={key}
                                                                    style={{
                                                                        display: 'flex',
                                                                        alignItems: 'start',
                                                                        gap: 8,
                                                                        marginBottom: 8
                                                                    }}
                                                                >
                                                                    <Form.Item
                                                                        {...restField}
                                                                        name={[name, 'amount']}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Введите сумму'
                                                                            }
                                                                        ]}
                                                                        style={{ flex: 1 }}
                                                                    >
                                                                        <InputNumber
                                                                            min={0}
                                                                            placeholder="Сумма, ₽"
                                                                            style={{ width: '100%' }}
                                                                            size="large"
                                                                        />
                                                                    </Form.Item>
                                                                    <Button size="large" onClick={() => remove(name)}>
                                                                        <DeleteOutlined />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                            <Button
                                                                type="primary"
                                                                onClick={() => add()}
                                                                size="large"
                                                                style={{ width: '100%' }}
                                                            >
                                                                + Добавить разаллокацию
                                                            </Button>
                                                        </>
                                                    )}
                                                </Form.List>
                                            ) : null
                                        }
                                    </Form.Item> */}
                                </>
                            )}
                        </div>
                    </Col>

                    <Col span={24}>
                        <Flex gap={6}>
                            <Button type="primary" onClick={onClose} size="large">
                                <CloseOutlined />
                            </Button>
                            <Button
                                type="primary"
                                style={{ flex: 1 }}
                                onClick={() => onSubmit({ state: 'draft' })}
                                size="large"
                            >
                                Сохранить
                                <LockOutlined />
                            </Button>
                            <Dropdown
                                key="more"
                                trigger={['click']}
                                menu={{
                                    items: moreActionsItems
                                }}
                            >
                                <Button type="primary" style={{ verticalAlign: 'bottom' }} size="large">
                                    <MoreOutlined style={{ color: colorWhite }} />
                                </Button>
                            </Dropdown>
                        </Flex>
                    </Col>
                </Row>

                <Form.Item<FieldType> name="state" hidden>
                    <Input />
                </Form.Item>
                <Form.Item<FieldType> name="isAdvertising" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="messages" hidden />
                <Form.Item<FieldType> label="Введите название поста" name="title" hidden initialValue={'Title'}>
                    <Input />
                </Form.Item>
            </Form>
        </Popup>
    )
}
